import { useEffect, useState } from "react";

import useUser from "@/users/useUser";
import getIdentifier from "./getIdentifier";
import getPollPayload from "./getPollPayload";
import {
  STATUS_WEBSOCKET_ATTEMPTING_TO_CONNECT,
  STATUS_WEBSOCKET_CLOSED,
  STATUS_WEBSOCKET_CONNECTED,
  STATUS_WEBSOCKET_ERROR,
  STATUS_WEBSOCKET_NOT_CONNECTED,
} from "./webSocketContants";

const TSOPHIEL_URL = process.env.NEXT_PUBLIC_TSOPHIEL_POLL_TRACKER_URL;
const RECONNECT_TIMEOUT_LENGTH = 1500;
const ALLOWED_RECONNECT_ATTEMPTS = 5;

const usePollResultsTracker = ({
  assignedPoll,
  setAssignedPoll,
  webSocketRef,
  getPoll,
}) => {
  const [webSocketStatus, setWebSocketStatus] = useState(
    STATUS_WEBSOCKET_NOT_CONNECTED,
  );
  const [webSocketReconnectAttempts, setWebSocketReconnectAttempts] =
    useState(0);
  const { data: user } = useUser();

  useEffect(() => {
    if (!assignedPoll || !assignedPoll?.assignment_id) {
      return;
    }

    // Handle initial WebSocket errors (in case it is blocked by district)
    try {
      webSocketRef.current = new WebSocket(TSOPHIEL_URL);
      setWebSocketStatus(STATUS_WEBSOCKET_ATTEMPTING_TO_CONNECT);
    } catch (error) {
      setWebSocketStatus(STATUS_WEBSOCKET_ERROR);
      const errorMessage = "Poll Websocket error: " + JSON.stringify(error);
      console.error(errorMessage);
      return;
    }

    webSocketRef.current.addEventListener("open", () => {
      setWebSocketStatus(STATUS_WEBSOCKET_CONNECTED);
      const messageProperties = {
        accessKey: getIdentifier(),
        assignmentId: assignedPoll?.assignment_id,
        userId: user?.id,
        userUniqueId: user?.unique_id,
      };

      const payload = getPollPayload(messageProperties);
      if (webSocketRef.current.readyState === WebSocket.OPEN) {
        webSocketRef.current.send(payload);
      }
    });

    webSocketRef.current.addEventListener("error", (error) => {
      setWebSocketStatus(STATUS_WEBSOCKET_ERROR);
      const errorMessage = "Poll Websocket error: " + JSON.stringify(error);
      console.error(errorMessage);
    });

    webSocketRef.current.addEventListener("close", () => {
      if (webSocketReconnectAttempts < ALLOWED_RECONNECT_ATTEMPTS) {
        setTimeout(() => {
          setWebSocketReconnectAttempts(
            (prevReconnectAttempts) => prevReconnectAttempts + 1,
          );
          const retrySocket = new WebSocket(TSOPHIEL_URL);
          webSocketRef.current = retrySocket;
        }, RECONNECT_TIMEOUT_LENGTH * webSocketReconnectAttempts);
      } else {
        setWebSocketStatus(STATUS_WEBSOCKET_CLOSED);
      }
    });

    webSocketRef.current.addEventListener("message", async (event) => {
      const { update_your_results_for_assignment: assignedId } = JSON.parse(
        event.data,
      );
      if (assignedPoll && assignedId === assignedPoll?.assignment_id) {
        const { data: pollData } = await getPoll({ pollId: assignedPoll?.id });
        setAssignedPoll(pollData);
      }
    });

    return () => {
      if (webSocketRef.current) {
        webSocketRef.current.close();
      }
    };
  }, [assignedPoll?.assignment_id, webSocketReconnectAttempts]);

  return {
    webSocketStatus,
  };
};

export default usePollResultsTracker;
